import { useState } from 'react'

import { WorkingTimePercentagesDocument } from 'generated/resources/graphql'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useLazyQuery } from 'modules/core/hooks/useQuery'

const CENTS = 100.0

export const useWorkingTimePercentages = ({
  employeeIds,
  fteEnabled,
}: {
  employeeIds?: number[]
  fteEnabled: boolean
}) => {
  const [allWorkingTimePercentages, setAllWorkingTimePercentages] = useState<{
    [key: number]: number | null | undefined
  }>({})
  const [finishedLoading, setFinishedLoading] = useState(false)

  const [fetchWorkingTimePercentages, { data }] = useLazyQuery(WorkingTimePercentagesDocument, {
    fetchPolicy: 'no-cache',
  })

  const hasMissingWorkingTimePercentage = useUnsafeMemo(() => {
    return Object.values(allWorkingTimePercentages).some((workingTimePercentageInCents) => {
      return workingTimePercentageInCents === null || workingTimePercentageInCents === undefined
    })
  }, [allWorkingTimePercentages])

  const averageHeadcount = useUnsafeMemo(() => {
    const validPercentages = Object.values(allWorkingTimePercentages).filter(
      (percentage) => percentage !== null && percentage !== undefined
    )

    const averageInCents = validPercentages.length
      ? validPercentages.reduce((a, b) => a + b, 0) / validPercentages.length
      : 0

    const average = averageInCents / CENTS
    const headCount = employeeIds?.length || 0

    return ((average / 100.0) * headCount).toFixed(2)
  }, [employeeIds, allWorkingTimePercentages])

  useUnsafeEffect(() => {
    setAllWorkingTimePercentages({})
    setFinishedLoading(employeeIds?.length === 0)

    if (!fteEnabled) return
    if (!employeeIds?.length) return

    fetchWorkingTimePercentages({
      variables: { employeeIds, after: null },
    })
  }, [fteEnabled, employeeIds])

  useUnsafeEffect(() => {
    if (finishedLoading) return
    if (!data?.contracts?.referenceContractsConnection) return

    if (hasMissingWorkingTimePercentage) {
      setFinishedLoading(true)
      return
    }

    const newPercentages: { [key: number]: number | null | undefined } = {}
    const connection = data.contracts.referenceContractsConnection
    connection.nodes?.forEach((node) => {
      if (node.id) newPercentages[node.id] = node.workingTimePercentageInCents
    })

    setAllWorkingTimePercentages((prevNodes) => ({ ...prevNodes, ...newPercentages }))

    if (connection.pageInfo.hasNextPage) {
      const nextCursor = connection.pageInfo.endCursor

      fetchWorkingTimePercentages({
        variables: {
          employeeIds: employeeIds ?? [],
          after: nextCursor,
        },
      })
    } else {
      setFinishedLoading(true)
    }
  }, [data, finishedLoading, hasMissingWorkingTimePercentage])

  return {
    averageHeadcount,
    loading: !finishedLoading,
    hasMissingWorkingTimePercentage,
  }
}
